import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import { apiService } from '../services/api';
import { Competition, NotificationAccessType, NotificationScope, SendNotificationRequest } from '../types/interfaces';
import { emitNotificationsUpdated } from '../utils/notificationEvents';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import '../components/Auth/RegisterModal.css';
import './ManageCompetitionModals.css';

interface NotificationComposerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSent?: () => void;
  competitions?: Competition[];
  fixedCompetitionId?: number;
  fixedCompetitionTitle?: string;
  allowScopeSelection?: boolean;
  title?: string;
}

const COMPETITION_RECIPIENT_OPTIONS: Array<{ value: NotificationAccessType; label: string }> = [
  { value: 'competitor', label: 'Competitors' },
  { value: 'judge', label: 'Judges' },
  { value: 'viewer', label: 'Viewers' },
  { value: 'host', label: 'Hosts' },
];

const defaultRecipientTypes: NotificationAccessType[] = ['competitor', 'judge'];

const parseRecipientIdentifiers = (raw: string): string[] => {
  const pieces = raw
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const piece of pieces) {
    const key = piece.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(piece);
  }
  return unique;
};

const NotificationComposerModal: React.FC<NotificationComposerModalProps> = ({
  isOpen,
  onClose,
  onSent,
  competitions = [],
  fixedCompetitionId,
  fixedCompetitionTitle,
  allowScopeSelection = true,
  title = 'Send Notification',
}) => {
  const [scope, setScope] = useState<NotificationScope>(fixedCompetitionId ? 'competition' : 'global');
  const [competitionId, setCompetitionId] = useState<string>('');
  const [recipientTypes, setRecipientTypes] = useState<NotificationAccessType[]>(defaultRecipientTypes);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [message, setMessage] = useState('');
  const [actionLabel, setActionLabel] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [recipientIdentifiersText, setRecipientIdentifiersText] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  const selectedCompetition = useMemo(() => {
    if (fixedCompetitionId) {
      return competitions.find((c) => Number(c.id) === Number(fixedCompetitionId)) || null;
    }
    if (!competitionId) return null;
    return competitions.find((c) => String(c.id) === competitionId) || null;
  }, [competitionId, competitions, fixedCompetitionId]);

  useEffect(() => {
    if (!isOpen) return;
    setScope(fixedCompetitionId ? 'competition' : 'global');
    if (fixedCompetitionId) {
      setCompetitionId(String(fixedCompetitionId));
    } else {
      setCompetitionId(competitions.length > 0 ? String(competitions[0].id) : '');
    }
    setRecipientTypes(defaultRecipientTypes);
    setNotificationTitle('');
    setMessage('');
    setActionLabel('');
    setActionUrl('');
    setRecipientIdentifiersText('');
    setDueAt('');
    setError('');
    setToastMessage('');
    setIsLoading(false);
  }, [isOpen, fixedCompetitionId, competitions]);

  useEscapeToClose(onClose, {
    enabled: isOpen && !isLoading,
    confirmIfDirty: () => [notificationTitle, message, actionLabel, actionUrl, recipientIdentifiersText, dueAt]
      .some((value) => value.trim() !== ''),
  });

  if (!isOpen) return null;

  const isCompetitionScope = scope === 'competition';

  const toggleRecipientType = (value: NotificationAccessType) => {
    setRecipientTypes((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value);
      }
      return [...prev, value];
    });
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const normalizedTitle = notificationTitle.trim();
    const normalizedMessage = message.trim();
    const normalizedActionLabel = actionLabel.trim();
    const normalizedActionUrl = actionUrl.trim();
    const specificIdentifiers = parseRecipientIdentifiers(recipientIdentifiersText);

    if (!normalizedTitle) {
      setError('Title is required.');
      return;
    }
    if (!normalizedMessage) {
      setError('Message is required.');
      return;
    }

    let resolvedCompetitionId: number | undefined;
    if (isCompetitionScope) {
      resolvedCompetitionId = Number(fixedCompetitionId || competitionId);
      if (!resolvedCompetitionId) {
        setError('Select a competition.');
        return;
      }
      if (!recipientTypes.length && !specificIdentifiers.length) {
        setError('Choose at least one competition recipient tier or provide specific users.');
        return;
      }
    }

    let normalizedDueAt: string | undefined;
    if (dueAt) {
      const parsed = new Date(dueAt);
      if (Number.isNaN(parsed.getTime())) {
        setError('Invalid due date/time format.');
        return;
      }
      normalizedDueAt = parsed.toISOString();
    }

    const payload: SendNotificationRequest = {
      scope,
      title: normalizedTitle,
      message: normalizedMessage,
    };

    if (resolvedCompetitionId) {
      payload.competition_id = resolvedCompetitionId;
      if (recipientTypes.length > 0) payload.recipient_access_types = recipientTypes;
    }
    if (specificIdentifiers.length > 0) payload.recipient_usernames = specificIdentifiers;

    if (normalizedActionLabel) payload.action_label = normalizedActionLabel;
    if (normalizedActionUrl) payload.action_url = normalizedActionUrl;
    if (normalizedDueAt) payload.due_at = normalizedDueAt;

    setIsLoading(true);
    try {
      const response = await apiService.post('/notifications/send', payload);
      const count = Number(response?.data?.recipient_count || 0);
      setToastMessage(`Notification sent to ${count} recipient${count === 1 ? '' : 's'}.`);
      setNotificationTitle('');
      setMessage('');
      setActionLabel('');
      setActionUrl('');
      setRecipientIdentifiersText('');
      setDueAt('');
      setRecipientTypes(defaultRecipientTypes);
      emitNotificationsUpdated();
      onSent?.();
    } catch (err: any) {
      const msg = err?.message || err?.response?.data?.detail || 'Failed to send notification';
      setError(String(msg));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="register-modal-backdrop" onClick={handleBackdropClick}>
        <div
          className="register-modal notifications-modal"
          role="dialog"
          aria-modal="true"
          style={{ width: 'min(900px, 96vw)', maxWidth: '900px' }}
        >
          <div className="register-modal__header">
            <h2 className="register-modal__title">{title}</h2>
            <button
              type="button"
              className="register-modal__close"
              onClick={onClose}
              aria-label="Close modal"
              disabled={isLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form className="register-modal__form" onSubmit={handleSubmit}>
            {error && (
              <div className="register-modal__error" role="alert">
                {error}
              </div>
            )}

            <div className="register-modal__fields notifications-modal__fields">
              {allowScopeSelection && !fixedCompetitionId && (
                <label className="input-wrapper notifications-modal__input-wrapper">
                  <span className="input__label">Scope</span>
                  <select
                    className="input input--default input--md"
                    value={scope}
                    onChange={(e) => setScope(e.target.value as NotificationScope)}
                    disabled={isLoading}
                  >
                    <option value="global">Global (all users)</option>
                    <option value="competition">Competition</option>
                  </select>
                </label>
              )}

              {isCompetitionScope && (
                <>
                  {fixedCompetitionId ? (
                    <div className="notifications-modal__context">
                      <strong>Competition:</strong>{' '}
                      {fixedCompetitionTitle || selectedCompetition?.title || `Competition #${fixedCompetitionId}`}
                    </div>
                  ) : (
                    <label className="input-wrapper notifications-modal__input-wrapper">
                      <span className="input__label">Competition</span>
                      <select
                        className="input input--default input--md"
                        value={competitionId}
                        onChange={(e) => setCompetitionId(e.target.value)}
                        disabled={isLoading || competitions.length === 0}
                      >
                        {competitions.map((competition) => (
                          <option key={competition.id} value={String(competition.id)}>
                            {competition.title} (id={competition.id})
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  <fieldset className="notifications-modal__recipient-fieldset">
                    <legend>Recipients (competition access tiers)</legend>
                    <div className="notifications-modal__recipient-grid">
                      {COMPETITION_RECIPIENT_OPTIONS.map((opt) => (
                        <label key={opt.value} className="notifications-modal__checkbox-row">
                          <input
                            type="checkbox"
                            checked={recipientTypes.includes(opt.value)}
                            onChange={() => toggleRecipientType(opt.value)}
                            disabled={isLoading}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </>
              )}

              <label className="input-wrapper notifications-modal__input-wrapper">
                <span className="input__label">Title</span>
                <input
                  className="input input--default input--md"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  placeholder="Short notification title"
                  disabled={isLoading}
                />
              </label>

              <label className="input-wrapper notifications-modal__input-wrapper">
                <span className="input__label">Message</span>
                <textarea
                  className="input input--default input--md notifications-modal__textarea"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write the message users will see in their notification center."
                  disabled={isLoading}
                />
              </label>

              <label className="input-wrapper notifications-modal__input-wrapper">
                <span className="input__label">Specific Users (optional)</span>
                <textarea
                  className="input input--default input--md notifications-modal__textarea notifications-modal__recipients"
                  value={recipientIdentifiersText}
                  onChange={(e) => setRecipientIdentifiersText(e.target.value)}
                  placeholder="netid1, netid2 or email1@illinois.edu"
                  disabled={isLoading}
                />
                <span className="input__helper">
                  For global notifications: if this is filled, only these users receive it. For competition notifications:
                  these users are added on top of selected tiers.
                </span>
              </label>

              <div className="notifications-modal__meta-grid">
                <label className="input-wrapper notifications-modal__input-wrapper">
                  <span className="input__label">Action Label (optional)</span>
                  <input
                    className="input input--default input--md"
                    value={actionLabel}
                    onChange={(e) => setActionLabel(e.target.value)}
                    placeholder="Open competition"
                    disabled={isLoading}
                  />
                </label>

                <label className="input-wrapper notifications-modal__input-wrapper">
                  <span className="input__label">Action URL (optional)</span>
                  <input
                    className="input input--default input--md"
                    value={actionUrl}
                    onChange={(e) => setActionUrl(e.target.value)}
                    placeholder="/competition/7"
                    disabled={isLoading}
                  />
                </label>
              </div>

              <label className="input-wrapper notifications-modal__input-wrapper">
                <span className="input__label">Due Date (optional)</span>
                <input
                  className="input input--default input--md"
                  type="datetime-local"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  disabled={isLoading}
                />
              </label>
            </div>

            <div className="register-modal__actions">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                Close
              </Button>
              <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                Send Notification
              </Button>
            </div>
          </form>
        </div>
      </div>

      {toastMessage && <Toast message={toastMessage} duration={2600} onClose={() => setToastMessage('')} />}
    </>
  );
};

export default NotificationComposerModal;
